import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Copy, Trash2, AlertTriangle, Megaphone } from 'lucide-react';
import { Badge, Button, Container, Dialog, Empty, Select, useToast } from '@easymoving/ui';
import type { Destination, ProgressStatus } from '@easymoving/types';
import { useBackend } from '../lib/backend/context';
import { useWorkspace } from '../context/WorkspaceContext';
import { useBoxes, useItems } from '../hooks/useUserData';
import { categoryLabel, conditionLabel, roomLabel } from '../lib/taxonomy/options';
import { destinationTone, statusTone } from '../lib/taxonomy/tone';
import { DestinationSelect, StatusSelect } from '../components/items/QuickSelects';
import { ItemImages } from '../components/items/ItemImages';
import { InlineMeasurements } from '../components/items/InlineMeasurements';
import { itemToDraft } from '../lib/items/draft';
import { itemLabel } from '../lib/items/label';
import { buildListingText } from '../lib/items/listing';

const NO_BOX = '__none__';

export function ItemDetailPage() {
  const { t } = useTranslation();
  const backend = useBackend();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { items, loading } = useItems();
  const { boxes } = useBoxes();
  const { canEdit } = useWorkspace();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const item = items.find((i) => i.id === itemId);

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }
  if (!item || !itemId) {
    return (
      <Container className="pt-10">
        <Empty title={t('notFound.title')} body={t('notFound.body')} />
      </Container>
    );
  }

  async function onDuplicate() {
    if (!itemId) return;
    const newId = await backend.duplicateItem(itemId);
    toast.push({ title: t('item.duplicated'), tone: 'success' });
    if (newId) navigate(`/app/items/${newId}`);
  }

  async function onDelete() {
    if (!itemId || !item) return;
    const draft = itemToDraft(item);
    await backend.deleteItem(itemId);
    void backend.deleteItemImages(itemId);
    navigate('/app/items');
    toast.push({
      title: t('item.deleted'),
      tone: 'success',
      action: { label: t('item.undo'), onClick: () => void backend.createItem(draft) },
    });
  }

  function quickUpdate(patch: Parameters<typeof backend.updateItem>[1]) {
    void backend
      .updateItem(itemId as string, patch)
      .then(() => toast.push({ title: t('item.updated'), tone: 'success' }));
  }

  /** Sell items: compose a ready-to-paste ad and share/copy it. */
  async function onCreateListing() {
    if (!item) return;
    const text = buildListingText(item, t, {
      conditionLabel: item.condition ? conditionLabel(item.condition, t) : undefined,
    });
    if (navigator.share) {
      await navigator.share({ text }).catch(() => undefined);
      return;
    }
    await navigator.clipboard?.writeText(text).catch(() => undefined);
    toast.push({ title: t('listing.copied'), tone: 'success' });
  }

  const boxOptions = [
    { value: NO_BOX, label: t('item.noBox') },
    ...boxes.map((b) => ({ value: b.id, label: b.name })),
  ];

  return (
    <Container size="narrow" className="pt-4 pb-8">
      <Link to="/app/items" className="text-petrol text-[14px] hover:underline">
        ← {t('item.toGallery')}
      </Link>

      <div className="mt-3 surface overflow-hidden">
        <ItemImages item={item} />
        <div className="p-5 flex flex-col gap-4">
          <div>
            <h1 className="font-display text-2xl tracking-snug">{itemLabel(item)}</h1>
            {item.nickname?.trim() && item.nickname.trim() !== item.name && (
              <p className="text-graphite-soft text-[13px] mt-0.5">{item.name}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge tone={destinationTone(item.destination)}>
                {t(`enum.destination.${item.destination}`)}
              </Badge>
              <Badge tone={statusTone(item.progressStatus)}>
                {t(`enum.status.${item.progressStatus}`)}
              </Badge>
              {item.fragile && (
                <Badge tone="amber">
                  <AlertTriangle className="size-3" />
                  {t('item.fragile')}
                </Badge>
              )}
            </div>
          </div>

          {/* Quick actions: change destination/status/box inline (read-only for viewers) */}
          {canEdit && (
            <div className="grid grid-cols-2 gap-3">
              <QuickField label={t('item.destination')}>
                <DestinationSelect
                  value={item.destination}
                  onValueChange={(v) => quickUpdate({ destination: v as Destination })}
                />
              </QuickField>
              <QuickField label={t('item.status')}>
                <StatusSelect
                  value={item.progressStatus}
                  onValueChange={(v) => quickUpdate({ progressStatus: v as ProgressStatus })}
                />
              </QuickField>
              <QuickField label={t('item.box')}>
                <Select
                  ariaLabel={t('item.box')}
                  value={item.boxId || NO_BOX}
                  options={boxOptions}
                  onValueChange={(v) => quickUpdate({ boxId: v === NO_BOX ? undefined : v })}
                />
              </QuickField>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-y-3 gap-x-4 text-[14px] border-t hairline pt-4">
            <Field label={t('item.room')} value={roomLabel(item.room, t)} />
            <Field label={t('item.category')} value={categoryLabel(item.category, t)} />
            <Field label={t('item.box')} value={boxes.find((b) => b.id === item.boxId)?.name} />
            {item.condition && <Field label={t('item.condition')} value={conditionLabel(item.condition, t)} />}
            {item.quantity > 1 && <Field label={t('item.quantity')} value={String(item.quantity)} />}
            {item.estimatedValue != null && (
              <Field
                label={t('item.value')}
                value={`${item.estimatedValue}${item.currency ? ` ${item.currency}` : ''}`}
              />
            )}
            {item.storageLocation && <Field label={t('item.storageLocation')} value={item.storageLocation} />}
          </dl>

          <InlineMeasurements item={item} />

          {item.notes && (
            <p className="text-graphite text-[14px] whitespace-pre-wrap border-t hairline pt-4">
              {item.notes}
            </p>
          )}

          {canEdit && (
            <div className="flex flex-wrap gap-2 border-t hairline pt-4">
              <Button asChild variant="secondary">
                <Link to={`/app/items/${item.id}/edit`}>
                  <Pencil className="size-4" />
                  {t('item.edit')}
                </Link>
              </Button>
              <Button variant="ghost" onClick={onDuplicate}>
                <Copy className="size-4" />
                {t('item.duplicate')}
              </Button>
              {item.destination === 'sell' && (
                <Button variant="ghost" onClick={() => void onCreateListing()}>
                  <Megaphone className="size-4" />
                  {t('listing.cta')}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="text-clay">
                <Trash2 className="size-4" />
                {t('item.delete')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('item.deleteConfirm')}
        description={t('item.deleteConfirmBody')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('item.cancel')}
            </Button>
            <Button variant="danger" onClick={onDelete}>
              {t('item.delete')}
            </Button>
          </div>
        }
      >
        <p className="text-graphite text-[14px]">{item.name}</p>
      </Dialog>
    </Container>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-graphite-soft text-[12px]">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function QuickField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[12px] text-graphite-soft">
      {label}
      {children}
    </label>
  );
}
